import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { User, Moon, Sun, LogOut, Shield, Bell, Mail, Phone, CheckCircle, RefreshCw, AlertCircle, Pencil, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const phoneCountries = [
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CH', name: 'Suisse', dialCode: '+41', flag: '🇨🇭' },
  { code: 'CM', name: 'Cameroun', dialCode: '+237', flag: '🇨🇲' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'GA', name: 'Gabon', dialCode: '+241', flag: '🇬🇦' },
  { code: 'CG', name: 'Congo', dialCode: '+242', flag: '🇨🇬' },
  { code: 'CD', name: 'RD Congo', dialCode: '+243', flag: '🇨🇩' },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226', flag: '🇧🇫' },
  { code: 'NE', name: 'Niger', dialCode: '+227', flag: '🇳🇪' },
  { code: 'TG', name: 'Togo', dialCode: '+228', flag: '🇹🇬' },
  { code: 'BJ', name: 'Bénin', dialCode: '+229', flag: '🇧🇯' },
];

function detectCountryFromPhone(phone) {
  if (!phone) return 'CA';
  const cleaned = phone.replace(/[\s-]/g, '');
  const sorted = [...phoneCountries].sort((a, b) => b.dialCode.length - a.dialCode.length);
  for (const c of sorted) {
    if (cleaned.startsWith(c.dialCode)) return c.code;
  }
  return 'CA';
}

function getLocalNumber(phone, countryCode) {
  if (!phone) return '';
  const country = phoneCountries.find(c => c.code === countryCode);
  if (!country) return phone;
  const cleaned = phone.replace(/[\s-]/g, '');
  if (cleaned.startsWith(country.dialCode)) {
    return cleaned.slice(country.dialCode.length);
  }
  if (!cleaned.startsWith('+')) return cleaned;
  return cleaned;
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout, refreshUser, API } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const securityRef = useRef(null);
  const needsVerification = user && (!user.email_verified || !user.phone_verified);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    address: user?.address || ''
  });

  const [editEmail, setEditEmail] = useState(user?.email || '');
  const [phoneCountry, setPhoneCountry] = useState(detectCountryFromPhone(user?.phone));
  const [localPhone, setLocalPhone] = useState(getLocalNumber(user?.phone, detectCountryFromPhone(user?.phone)));
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [secLoading, setSecLoading] = useState({ emailSave: false, phoneSave: false, emailVerify: false, phoneVerify: false, resendEmail: false, resendPhone: false });
  const [resendEmailCooldown, setResendEmailCooldown] = useState(0);
  const [resendPhoneCooldown, setResendPhoneCooldown] = useState(0);

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  useEffect(() => {
    if (resendEmailCooldown > 0) {
      const t = setTimeout(() => setResendEmailCooldown(resendEmailCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendEmailCooldown]);

  useEffect(() => {
    if (resendPhoneCooldown > 0) {
      const t = setTimeout(() => setResendPhoneCooldown(resendPhoneCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendPhoneCooldown]);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) return "L'email est requis";
    if (!re.test(email)) return "Format d'email invalide (ex: nom@exemple.com)";
    return '';
  };

  const validatePhone = (phone) => {
    const re = /^\+?[0-9]{8,15}$/;
    if (!phone) return 'Le téléphone est requis';
    if (!re.test(phone.replace(/[\s-]/g, ''))) return 'Format invalide. Entrez un numéro valide (ex: 4381234567)';
    return '';
  };

  const getFullPhone = () => {
    const country = phoneCountries.find(c => c.code === phoneCountry);
    const dial = country ? country.dialCode : '+1';
    const cleaned = localPhone.replace(/[\s-]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    return `${dial}${cleaned}`;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/users/me`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async () => {
    const err = validateEmail(editEmail);
    if (err) { setEmailError(err); return; }
    if (editEmail === user?.email) { setEmailError(''); setIsEditingEmail(false); return; }
    setSecLoading(prev => ({ ...prev, emailSave: true }));
    try {
      await axios.put(`${API}/users/me`, { email: editEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      toast.success('Email modifié. Un code de vérification a été envoyé.');
      setEmailError('');
      setIsEditingEmail(false);
      setResendEmailCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSecLoading(prev => ({ ...prev, emailSave: false }));
    }
  };

  const handlePhoneChange = async () => {
    const fullPhone = getFullPhone();
    const err = validatePhone(fullPhone);
    if (err) { setPhoneError(err); return; }
    if (fullPhone === user?.phone) { setPhoneError(''); setIsEditingPhone(false); return; }
    setSecLoading(prev => ({ ...prev, phoneSave: true }));
    try {
      await axios.put(`${API}/users/me`, { phone: fullPhone }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      toast.success('Téléphone modifié. Un code de vérification a été envoyé.');
      setPhoneError('');
      setIsEditingPhone(false);
      setResendPhoneCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSecLoading(prev => ({ ...prev, phoneSave: false }));
    }
  };

  const verifyEmail = async () => {
    if (!emailCode) return;
    setSecLoading(prev => ({ ...prev, emailVerify: true }));
    try {
      await axios.post(`${API}/auth/verify-email`, { code: emailCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email vérifié !');
      setEmailCode('');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setSecLoading(prev => ({ ...prev, emailVerify: false }));
    }
  };

  const verifyPhone = async () => {
    if (!phoneCode) return;
    setSecLoading(prev => ({ ...prev, phoneVerify: true }));
    try {
      await axios.post(`${API}/auth/verify-phone`, { code: phoneCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Téléphone vérifié !');
      setPhoneCode('');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setSecLoading(prev => ({ ...prev, phoneVerify: false }));
    }
  };

  const resendEmailCode = async () => {
    setSecLoading(prev => ({ ...prev, resendEmail: true }));
    try {
      const res = await axios.post(`${API}/auth/resend-email-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.email_sent) {
        toast.success(res.data.message);
      } else {
        toast.info(`Code: ${res.data.code}`, { duration: 10000, description: 'Email non envoyé - code affiché pour test' });
      }
      if (res.data.next_delay_seconds) setResendEmailCooldown(res.data.next_delay_seconds);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSecLoading(prev => ({ ...prev, resendEmail: false }));
    }
  };

  const resendPhoneCode = async () => {
    setSecLoading(prev => ({ ...prev, resendPhone: true }));
    try {
      const res = await axios.post(`${API}/auth/resend-phone-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.sms_sent) {
        toast.success(res.data.message);
      } else {
        toast.info(`Code: ${res.data.code}`, { duration: 10000, description: 'SMS non envoyé - code affiché pour test' });
      }
      if (res.data.next_delay_seconds) setResendPhoneCooldown(res.data.next_delay_seconds);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSecLoading(prev => ({ ...prev, resendPhone: false }));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Auto-scroll to security section and show dialog when redirected for verification
  useEffect(() => {
    if ((location.hash === '#security' || needsVerification) && securityRef.current) {
      if (location.hash === '#security' && needsVerification) {
        setShowVerificationDialog(true);
      }
      setTimeout(() => {
        securityRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [location.hash, needsVerification]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Verification Required Dialog */}
      <Dialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <DialogContent className="sm:max-w-md" data-testid="verification-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              {"Vérification requise"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-left space-y-3 pt-2">
                <p className="text-foreground font-medium">
                  {"Votre compte n'est pas encore complètement vérifié."}
                </p>
                <p className="text-sm text-muted-foreground">
                  {"Pour pouvoir publier des annonces ou passer des commandes sur Kacos, vous devez vérifier au moins l'un des éléments suivants :"}
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  {!user?.email_verified && (
                    <li className="text-red-600 font-medium">{"Votre adresse email"}</li>
                  )}
                  {!user?.phone_verified && (
                    <li className="text-red-600 font-medium">{"Votre numéro de téléphone"}</li>
                  )}
                </ul>
                <p className="text-sm text-muted-foreground">
                  {"Un code de vérification vous a été envoyé. Entrez-le dans les champs en rouge ci-dessous pour activer votre compte."}
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowVerificationDialog(false)} data-testid="verification-dialog-ok-btn">
              {"Compris, je vérifie maintenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-3xl mx-auto px-4 py-24">
        <h1 className="font-heading text-3xl font-bold mb-8">{"Paramètres"}</h1>

        <div className="space-y-6">
          {/* Profile Settings */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                {"Informations personnelles"}
              </CardTitle>
              <CardDescription>
                {"Mettez à jour vos informations de profil"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{"Prénom"}</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="settings-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{"Nom"}</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    data-testid="settings-lastname"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{"Adresse"}</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="settings-address"
                />
              </div>

              <Button 
                onClick={handleSave} 
                disabled={loading}
                data-testid="settings-save"
              >
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {theme === 'dark' ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
                {"Apparence"}
              </CardTitle>
              <CardDescription>
                {"Personnalisez l'apparence de l'application"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{"Mode sombre"}</p>
                  <p className="text-sm text-muted-foreground">
                    {"Activer le thème sombre"}
                  </p>
                </div>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                  data-testid="theme-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card className="card-dashboard" ref={securityRef} id="security">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                {"Sécurité"}
              </CardTitle>
              <CardDescription>
                {"Gérez la sécurité de votre compte. La vérification de votre email ou téléphone est requise pour passer des commandes."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Verification */}
              <div className={`rounded-lg border p-4 ${user?.email_verified ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/50 bg-red-500/5'}`} data-testid="security-email-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Mail className={`w-4 h-4 ${user?.email_verified ? 'text-green-500' : 'text-red-500'}`} />
                    <span className="font-medium">{"Vérification email"}</span>
                  </div>
                  {user?.email_verified ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-green-500" data-testid="email-verified-badge">
                      <CheckCircle className="w-4 h-4" /> {"Vérifié"}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm font-medium text-red-500" data-testid="email-not-verified-badge">
                      <AlertCircle className="w-4 h-4" /> {"Non vérifié"}
                    </span>
                  )}
                </div>
                
                <div className="space-y-2">
                  {user?.email_verified && !isEditingEmail ? (
                    <div>
                      <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                        <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm font-medium" data-testid="email-display">{user?.email}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => setIsEditingEmail(true)}
                        data-testid="email-edit-btn"
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        {"Modifier"}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={editEmail}
                          onChange={(e) => { setEditEmail(e.target.value); setEmailError(''); }}
                          placeholder="votre@email.com"
                          className="flex-1"
                          data-testid="settings-email-input"
                        />
                        {(editEmail !== user?.email || isEditingEmail) && (
                          <Button
                            size="sm"
                            onClick={handleEmailChange}
                            disabled={secLoading.emailSave}
                            data-testid="settings-email-save-btn"
                          >
                            {secLoading.emailSave ? 'Envoi...' : 'Sauvegarder'}
                          </Button>
                        )}
                        {isEditingEmail && user?.email_verified && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setIsEditingEmail(false); setEditEmail(user?.email || ''); setEmailError(''); }}
                            data-testid="email-cancel-edit-btn"
                          >
                            {"Annuler"}
                          </Button>
                        )}
                      </div>
                      {emailError && <p className="text-xs text-red-500" data-testid="email-error">{emailError}</p>}
                    </div>
                  )}
                </div>

                {!user?.email_verified && (
                  <div className="mt-3 space-y-2 rounded-lg p-3 border-2 border-red-500 bg-red-500/5 animate-pulse-red" data-testid="email-code-section">
                    <p className="text-xs font-semibold text-red-600 mb-1">{"Entrez votre code de vérification :"}</p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Entrez le code reçu par email"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value.toUpperCase())}
                        className="flex-1 border-2 border-red-400 bg-red-50 dark:bg-red-950/20 text-lg font-mono tracking-widest placeholder:text-sm placeholder:tracking-normal placeholder:font-normal"
                        data-testid="settings-email-code-input"
                      />
                      <Button
                        onClick={verifyEmail}
                        disabled={secLoading.emailVerify || !emailCode}
                        data-testid="settings-email-verify-btn"
                      >
                        {secLoading.emailVerify ? 'Vérification...' : 'Vérifier'}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">{"Vérifiez votre boîte de réception et vos spams"}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resendEmailCode}
                        disabled={secLoading.resendEmail || resendEmailCooldown > 0}
                        className="text-red-600 text-xs"
                        data-testid="settings-resend-email-btn"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${secLoading.resendEmail ? 'animate-spin' : ''}`} />
                        {resendEmailCooldown > 0 ? `Attendre ${resendEmailCooldown}s` : 'Renvoyer le code'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Phone Verification */}
              <div className={`rounded-lg border p-4 ${user?.phone_verified ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/50 bg-red-500/5'}`} data-testid="security-phone-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Phone className={`w-4 h-4 ${user?.phone_verified ? 'text-green-500' : 'text-red-500'}`} />
                    <span className="font-medium">{"Vérification téléphone"}</span>
                  </div>
                  {user?.phone_verified ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-green-500" data-testid="phone-verified-badge">
                      <CheckCircle className="w-4 h-4" /> {"Vérifié"}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm font-medium text-red-500" data-testid="phone-not-verified-badge">
                      <AlertCircle className="w-4 h-4" /> {"Non vérifié"}
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  {user?.phone_verified && !isEditingPhone ? (
                    <div>
                      <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                        <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm font-medium" data-testid="phone-display">{user?.phone}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => setIsEditingPhone(true)}
                        data-testid="phone-edit-btn"
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        {"Modifier"}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <select
                          value={phoneCountry}
                          onChange={(e) => setPhoneCountry(e.target.value)}
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          data-testid="phone-country-select"
                        >
                          {phoneCountries.map(c => (
                            <option key={c.code} value={c.code}>
                              {c.flag} {c.dialCode}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="tel"
                          value={localPhone}
                          onChange={(e) => { setLocalPhone(e.target.value); setPhoneError(''); }}
                          placeholder="4381234567"
                          className="flex-1"
                          data-testid="settings-phone-input"
                        />
                        <Button
                          size="sm"
                          onClick={handlePhoneChange}
                          disabled={secLoading.phoneSave}
                          data-testid="settings-phone-save-btn"
                        >
                          {secLoading.phoneSave ? 'Envoi...' : 'Sauvegarder'}
                        </Button>
                        {isEditingPhone && user?.phone_verified && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setIsEditingPhone(false);
                              setPhoneCountry(detectCountryFromPhone(user?.phone));
                              setLocalPhone(getLocalNumber(user?.phone, detectCountryFromPhone(user?.phone)));
                              setPhoneError('');
                            }}
                            data-testid="phone-cancel-edit-btn"
                          >
                            {"Annuler"}
                          </Button>
                        )}
                      </div>
                      {phoneError && <p className="text-xs text-red-500" data-testid="phone-error">{phoneError}</p>}
                    </div>
                  )}
                </div>

                {!user?.phone_verified && (
                  <div className="mt-3 space-y-2 rounded-lg p-3 border-2 border-red-500 bg-red-500/5 animate-pulse-red" data-testid="phone-code-section">
                    <p className="text-xs font-semibold text-red-600 mb-1">{"Entrez votre code de vérification :"}</p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Entrez le code reçu par SMS"
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value.toUpperCase())}
                        className="flex-1 border-2 border-red-400 bg-red-50 dark:bg-red-950/20 text-lg font-mono tracking-widest placeholder:text-sm placeholder:tracking-normal placeholder:font-normal"
                        data-testid="settings-phone-code-input"
                      />
                      <Button
                        onClick={verifyPhone}
                        disabled={secLoading.phoneVerify || !phoneCode}
                        data-testid="settings-phone-verify-btn"
                      >
                        {secLoading.phoneVerify ? 'Vérification...' : 'Vérifier'}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">{"Un code a été envoyé à votre numéro"}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resendPhoneCode}
                        disabled={secLoading.resendPhone || resendPhoneCooldown > 0}
                        className="text-red-600 text-xs"
                        data-testid="settings-resend-phone-btn"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${secLoading.resendPhone ? 'animate-spin' : ''}`} />
                        {resendPhoneCooldown > 0 ? `Attendre ${resendPhoneCooldown}s` : 'Renvoyer le code'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="card-dashboard border-destructive/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <LogOut className="w-5 h-5" />
                {"Déconnexion"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                variant="destructive" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                {"Se déconnecter"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
